import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'next-i18next'
import { graphqlAPI } from '../utils/graphql'
import { formatDate, formatCurrency, calculateEventPricing, calculateOrderTotal, isEventAvailable } from '../utils/helpers'
import config from '../config/config'
import './TicketSelection.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faClock, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons'

const TicketSelection = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [quantity, setQuantity] = useState('')
  const [pricing, setPricing] = useState(null)
  const [isAvailable, setIsAvailable] = useState(false)

  const loadEvent = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [eventData, availabilityData] = await Promise.all([
        graphqlAPI.getEventById(eventId),
        graphqlAPI.getTicketAvailability(eventId)
      ])

      if (!eventData) {
        throw new Error(t('errors.notFound'))
      }
      
      // Check capacity and override isSoldOut if needed
      if (availabilityData) {
        if (availabilityData.remainingTickets !== null && availabilityData.remainingTickets !== undefined) {
          eventData.remainingTickets = availabilityData.remainingTickets
          if (availabilityData.remainingTickets <= 0) {
            eventData.isSoldOut = true
          }
        }
      }

      setEvent(eventData)
      
      const pricingData = calculateEventPricing(eventData)
      setPricing(pricingData)
      
      // Check if event is available for purchase
      const available = isEventAvailable(eventData)
      setIsAvailable(available)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [eventId, t])

  useEffect(() => {
    loadEvent()
  }, [loadEvent])

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value)
    const maxLimit = event && event.remainingTickets !== null && event.remainingTickets !== undefined
      ? Math.min(config.TICKETS.MAX_TICKETS_PER_ORDER, event.remainingTickets)
      : config.TICKETS.MAX_TICKETS_PER_ORDER

    if (value >= 1 && value <= maxLimit) {
      setQuantity(value)
    }
  }

  const getUnavailabilityMessage = () => {
    if (!event) return t('tickets.eventUnavailable')
    
    if (event.isSoldOut) {
      return t('tickets.eventSoldOut')
    }
    
    if (!event.open) {
      return t('tickets.eventNotOpen')
    }
    
    // If event has no date or invalid date, treat as unavailable
    if (!event.date) {
      return t('tickets.eventNotOpen')
    }
    
    // Event has passed
    const now = new Date()
    const eventDate = new Date(event.date)
    
    // Check if the date is valid
    if (isNaN(eventDate.getTime()) || eventDate <= now) {
      return t('tickets.eventUnavailable')
    }
    
    return t('tickets.eventUnavailable')
  }

  const handleContinue = () => {
    // Store selection in sessionStorage for the verification process
    const orderData = {
      eventId: event.id,
      eventName: event.name,
      quantity,
      pricePerTicket: pricing.currentPrice,
      total: calculateOrderTotal(pricing.currentPrice, quantity),
      priceType: pricing.priceType
    }
    
    sessionStorage.setItem('ticketOrder', JSON.stringify(orderData))
    navigate('/verify-email')
  }

  if (loading) {
    return (
      <div className="ticket-selection">
        <div className="container">
          <div className="text-center">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="ticket-selection">
        <div className="container">
          <div className="alert alert-danger text-center">
            <h4>{t('common.error')}</h4>
            <p>{error || t('errors.notFound')}</p>
            <Link to="/" className="btn btn-primary">
              {t('tickets.backToEvents')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const total = quantity && quantity !== '' ? calculateOrderTotal(pricing.currentPrice, parseInt(quantity)) : 0

  return (
    <div className="ticket-selection">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Event Info */}
            <div className="event-info card mb-4">
              <div className="card-body">
                <h2 className="card-title">{event.name}</h2>
                
                <div className="event-details">
                  {event.date && (
                    <div className="detail-item">
                      <FontAwesomeIcon icon={faCalendarAlt} className='fas'/>
                      <span>{formatDate(event.date)}</span>
                    </div>
                  )}
                  
                  {event.start_time && (
                    <div className="detail-item">
                      <FontAwesomeIcon icon={faClock} className="fas"/>
                      <span>{event.start_time}</span>
                    </div>
                  )}
                  
                  {event.venue_name && (
                    <div className="detail-item">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="fas"/>
                      <span>{event.venue_name}</span>
                    </div>
                  )}
                </div>

                {event.meetup_id && (
                  <div className="detail-item meetup-link right">
                    <a
                      href={`https://www.meetup.com/es-ES/madrid-pianomeetups/events/${event.meetup_id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="meetup-link"
                    >
                      <i className="fab fa-meetup me-2"></i>
                      {t('tickets.viewOnMeetup')}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Ticket Selection */}
            <div className="ticket-form card">
              <div className="card-body">
                <h3>{t('tickets.title')}</h3>
                <p className="detail-item mb-4">
                  {t('tickets.subtitle', { eventName: event.name })}
                </p>

                {/* Event Availability Check */}
                {!isAvailable ? (
                  <div className="alert alert-danger mb-4">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {getUnavailabilityMessage()}
                  </div>
                ) : (
                  /* Pricing Info */
                  <div className="pricing-info mb-4">
                    {pricing.isEarlyBird ? (
                      <div className="alert alert-success">
                        <i className="fas fa-tag me-2"></i>
                        {t('tickets.earlyBirdInfo', { 
                          date: formatDate(pricing.earlyBirdCutoff) 
                        })}
                      </div>
                    ) : (
                      <div className="alert alert-warning">
                        <i className="fas fa-clock me-2"></i>
                        {t('tickets.regularPriceInfo')}
                      </div>
                    )}
                  </div>
                )}

                {/* Ticket Selection Form - Only show if event is available */}
                {isAvailable && (
                  <>
                    {/* Quantity Selection */}
                    <div className="form-group mb-4">
                      <label htmlFor="quantity" className="form-label">
                        {t('tickets.quantity')}
                      </label>
                      <div className="select-wrapper">
                        <select
                          id="quantity"
                          className="form-control"
                          value={quantity}
                          onChange={handleQuantityChange}
                          required
                        >
                          <option value="" disabled>
                            {t('tickets.selectQuantity')}
                          </option>
                          {Array.from({ length: event.remainingTickets !== null && event.remainingTickets !== undefined 
                            ? Math.min(config.TICKETS.MAX_TICKETS_PER_ORDER, event.remainingTickets)
                            : config.TICKETS.MAX_TICKETS_PER_ORDER 
                          }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1} {i === 0 ? t('tickets.ticketSingular') : t('tickets.ticketPlural')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <small className="form-text detail-item">
                        {t('tickets.maxTickets', { max: config.TICKETS.MAX_TICKETS_PER_ORDER })}
                      </small>
                    </div>
                    {/* Price Summary */}
                    <div className="price-summary">
                      <div className="summary-row">
                        <span>{t('tickets.pricePerTicket')}:</span>
                        <span className="price">{formatCurrency(pricing.currentPrice)}</span>
                      </div>
                      
                      <div className="summary-row">
                        <span>{t('tickets.quantity')}:</span>
                        <span>{quantity}</span>
                      </div>
                      
                      <div className="summary-row total">
                        <span>{t('tickets.total')}:</span>
                        <span className="price">{formatCurrency(total)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="form-actions">
                      <Link to="/" className="back-link left me-3">
                        {t('tickets.backToEvents')}
                      </Link>
                      <button
                        className="btn btn-primary"
                        onClick={handleContinue}
                        disabled={!quantity || quantity === ''}
                      >
                        {t('tickets.continue')}
                      </button>
                    </div>
                  </>
                )}

                {/* Show back button even when event is not available */}
                {!isAvailable && (
                  <div className="form-actions">
                    <Link to="/" className="btn btn-outline">
                      {t('tickets.backToEvents')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TicketSelection
